import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { statusMeta, giorniLabel } from "@/lib/scadenze";
import { formatDate } from "@/lib/format";
import {
  Building2,
  Handshake,
  MapPin,
  Package,
  Truck,
  Users,
  Loader2,
  AlertTriangle,
} from "lucide-react";

const KPI = [
  { key: "veicoli", label: "Veicoli", icon: Truck, to: "/veicoli" },
  { key: "autisti", label: "Autisti", icon: Users, to: "/autisti" },
  { key: "clienti", label: "Clienti", icon: Building2, to: "/clienti" },
  { key: "subvettori", label: "Sub-vettori", icon: Handshake, to: "/subvettori" },
  { key: "localita", label: "Località", icon: MapPin, to: "/localita" },
  { key: "prodotti", label: "Prodotti", icon: Package, to: "/prodotti" },
];

const BUCKETS = [
  { key: "rosso", label: "Scaduti", cls: "badge-rosso" },
  { key: "arancione", label: "Entro 30 giorni", cls: "badge-arancione" },
  { key: "giallo", label: "Entro 60 giorni", cls: "badge-giallo" },
  { key: "verde", label: "In regola", cls: "badge-verde" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="space-y-5" data-testid="dashboard">
      {/* KPI anagrafiche */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {KPI.map((k) => {
          const Icon = k.icon;
          return (
            <button
              key={k.key}
              onClick={() => navigate(k.to)}
              data-testid={`kpi-${k.key}`}
              className="bg-white border border-slate-200 rounded-md p-4 text-left hover:border-sky-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  {k.label}
                </span>
                <Icon className="h-4 w-4 text-sky-600" />
              </div>
              <div className="text-2xl font-head font-bold text-slate-900 mt-2 tabular-nums">
                {data.counts[k.key] ?? 0}
              </div>
            </button>
          );
        })}
      </div>

      {/* Scadenze buckets */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wider">
          Riepilogo scadenze
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {BUCKETS.map((b) => (
            <div
              key={b.key}
              className={`rounded-md p-4 border ${b.cls}`}
              data-testid={`bucket-${b.key}`}
            >
              <div className="text-xs font-semibold uppercase tracking-wider opacity-80">
                {b.label}
              </div>
              <div className="text-3xl font-head font-bold mt-1 tabular-nums">
                {data.scadenze_buckets[b.key] ?? 0}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <h2 className="text-sm font-semibold text-slate-700">
            Prossime scadenze (entro 60 giorni e scadute)
          </h2>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-2">Riferimento</th>
                <th className="text-left px-4 py-2">Tipo</th>
                <th className="text-left px-4 py-2">Categoria</th>
                <th className="text-left px-4 py-2">Scadenza</th>
                <th className="text-left px-4 py-2">Stato</th>
              </tr>
            </thead>
            <tbody>
              {data.upcoming.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-slate-400 text-sm">
                    Nessuna scadenza imminente
                  </td>
                </tr>
              ) : (
                data.upcoming.map((s) => {
                  const sm = statusMeta(s.status);
                  return (
                    <tr
                      key={s.id}
                      className={`border-t border-slate-100 hover:bg-slate-50 ${sm.row}`}
                      data-testid="upcoming-row"
                    >
                      <td className="px-4 py-2 font-medium text-slate-800">{s.parent_label}</td>
                      <td className="px-4 py-2 text-slate-600">{s.tipo}</td>
                      <td className="px-4 py-2 text-slate-500 capitalize">
                        {s.entity_type === "veicolo" ? "Mezzo" : "Autista"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {formatDate(s.data_scadenza)}
                        <span className="text-xs text-slate-400 ml-2">
                          {giorniLabel(s.giorni)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`scad-badge ${sm.cls}`}>{sm.label}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
